import React, { useState } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import LanguageSwitcher from '../components/LanguageSwitcher';
import './Login.css';

function ResetPassword() {
    const { t, i18n } = useTranslation();
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const token = searchParams.get('token');

    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [successMessage, setSuccessMessage] = useState('');
    const [errorMessage, setErrorMessage] = useState('');
    const API_BASE = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/$/, '') || '';

    const handleSubmit = async () => {
        setErrorMessage('');

        if (!newPassword || !confirmPassword) {
            setErrorMessage(t('password.allFieldsRequired', 'Veuillez remplir tous les champs.'));
            return;
        }

        if (newPassword !== confirmPassword) {
            setErrorMessage(t('password.passwordsNotMatch', 'Les mots de passe ne correspondent pas.'));
            return;
        }

        if (newPassword.length < 6) {
            setErrorMessage(t('password.passwordTooShort', 'Le mot de passe doit contenir au moins 6 caractères.'));
            return;
        }

        setIsLoading(true);

        try {
            const response = await fetch(`${API_BASE}/auth/reset-password`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ token, new_password: newPassword }),
            });

            if (!response.ok) {
                const data = await response.json();
                if (data.detail === 'Token expired') {
                    throw new Error('expired');
                }
                throw new Error(data.detail || 'Reset failed');
            }

            setSuccessMessage(t('auth.passwordResetSuccess', 'Votre mot de passe a été réinitialisé avec succès.'));

            // Redirect to login after 3s
            setTimeout(() => {
                navigate(`/${i18n.language}/login`, { replace: true });
            }, 3000);
        } catch (error: any) {
            if (error.message === 'expired') {
                setErrorMessage(t('auth.tokenExpired', 'Ce lien a expiré. Veuillez demander un nouveau lien de réinitialisation.'));
            } else {
                setErrorMessage(t('auth.resetFailed', 'Échec de la réinitialisation. Veuillez réessayer.'));
            }
        } finally {
            setIsLoading(false);
        }
    };

    const handleKeyPress = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter') {
            handleSubmit();
        }
    };

    // No token in URL
    if (!token) {
        return (
            <div className="login-page">
                <div className="container py-4">
                    <div className="login-card">
                        <div className="login-icon mb-3">
                            <i className="bi bi-exclamation-triangle text-danger"></i>
                        </div>
                        <h2 className="text-center mb-2">{t('auth.invalidLink', 'Lien invalide')}</h2>
                        <p className="text-center text-muted mb-4">
                            {t('auth.invalidResetLink', 'Ce lien de réinitialisation est invalide ou a expiré.')}
                        </p>
                        <Link to={`/${i18n.language}/forgot-password`} className="btn btn-primary w-100">
                            <i className="bi bi-arrow-repeat me-2"></i>
                            {t('auth.requestNewLink', 'Demander un nouveau lien')}
                        </Link>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="login-page">
            <div className="container py-4">
                <div className="login-card">
                    <div className="d-flex justify-content-between align-items-center mb-4">
                        <Link to={`/${i18n.language}/login`} className="back-link">
                            <i className="bi bi-arrow-left me-1"></i>
                            {t('auth.backToLogin', 'Retour à la connexion')}
                        </Link>
                        <LanguageSwitcher variant="white-bg" />
                    </div>

                    {/* Icon */}
                    <div className="login-icon mb-3">
                        <i className="bi bi-shield-lock"></i>
                    </div>

                    <h2 className="text-center mb-2">{t('auth.resetPasswordTitle', 'Nouveau mot de passe')}</h2>
                    <p className="text-center text-muted mb-4">
                        {t('auth.resetPasswordSubtitle', 'Saisissez votre nouveau mot de passe.')}
                    </p>

                    {/* Success Message */}
                    {successMessage && (
                        <div className="alert alert-success">
                            <i className="bi bi-check-circle me-2"></i>
                            {successMessage}
                            <p className="mt-2 mb-0 small">
                                {t('auth.redirectingToLogin', 'Redirection vers la page de connexion...')}
                            </p>
                        </div>
                    )}

                    {/* Error Message */}
                    {errorMessage && (
                        <div className="alert alert-danger alert-dismissible fade show" role="alert">
                            <i className="bi bi-exclamation-triangle me-2"></i>{errorMessage}
                            <button type="button" className="btn-close" onClick={() => setErrorMessage('')}></button>
                        </div>
                    )}

                    {/* Form */}
                    {!successMessage && (
                        <>
                            <div className="mb-3">
                                <label className="form-label">{t('password.newPassword', 'Nouveau mot de passe')}</label>
                                <div className="input-group">
                                    <span className="input-group-text">
                                        <i className="bi bi-lock"></i>
                                    </span>
                                    <input
                                        type={showPassword ? 'text' : 'password'}
                                        className="form-control"
                                        placeholder="••••••••"
                                        value={newPassword}
                                        onChange={(e) => setNewPassword(e.target.value)}
                                        onKeyPress={handleKeyPress}
                                    />
                                    <button
                                        className="btn btn-outline-secondary toggle-password"
                                        type="button"
                                        onClick={() => setShowPassword(!showPassword)}
                                    >
                                        <i className={`bi ${showPassword ? 'bi-eye-slash' : 'bi-eye'}`}></i>
                                    </button>
                                </div>
                            </div>

                            <div className="mb-4">
                                <label className="form-label">{t('password.confirmPassword', 'Confirmer le mot de passe')}</label>
                                <div className="input-group">
                                    <span className="input-group-text">
                                        <i className="bi bi-lock-fill"></i>
                                    </span>
                                    <input
                                        type={showPassword ? 'text' : 'password'}
                                        className="form-control"
                                        placeholder="••••••••"
                                        value={confirmPassword}
                                        onChange={(e) => setConfirmPassword(e.target.value)}
                                        onKeyPress={handleKeyPress}
                                    />
                                </div>
                            </div>

                            <button
                                onClick={handleSubmit}
                                className="btn btn-primary w-100 login-btn"
                                disabled={isLoading}
                            >
                                {isLoading ? (
                                    <>
                                        <span className="spinner-border spinner-border-sm me-2"></span>
                                        {t('common.loading')}
                                    </>
                                ) : (
                                    <>
                                        <i className="bi bi-check-lg me-2"></i>
                                        {t('auth.resetPasswordButton', 'Réinitialiser le mot de passe')}
                                    </>
                                )}
                            </button>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
}

export default ResetPassword;
